import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'localization';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import {
  getSubscribedSpheres,
  fetchSphereNames,
  unsubscribeSphereFromChannel,
  SubscribedSphereItem,
} from '../../../api/marketplaceApi';
import './SubscribedSpheresListOrganism.scss';

export interface SubscribedSpheresListOrganismProps {
  channelId: string;
  organizationId: string;
  onUnsubscribed?: () => void;
  onSphereIdsChanged?: (ids: string[]) => void;
  refreshTrigger?: number;
}

interface EnrichedSubscription extends SubscribedSphereItem {
  sphereName: string;
}

const PAGE_LIMIT = 20;

const SubscribedSpheresListOrganism: React.FC<SubscribedSpheresListOrganismProps> = ({
  channelId,
  organizationId,
  onUnsubscribed,
  onSphereIdsChanged,
  refreshTrigger = 0,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const orgId = organizationId || impersonationDetails?.organizationId || '';

  const [items, setItems] = useState<EnrichedSubscription[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const offsetRef = useRef(0);
  const hasNextPage = items.length < total;

  useEffect(() => {
    onSphereIdsChanged?.(items.map((s) => s.sphere_id));
  }, [items, onSphereIdsChanged]);

  const enrichWithNames = useCallback(
    async (subscriptions: SubscribedSphereItem[]): Promise<EnrichedSubscription[]> => {
      if (!apiClients?.graphClient || subscriptions.length === 0) {
        return subscriptions.map((s) => ({
          ...s,
          sphereName: t('sphereChannels.subscribedSpheres.unknownSphere'),
        }));
      }

      const sphereIds = subscriptions.map((s) => s.sphere_id);
      const nameMap = await fetchSphereNames(apiClients.graphClient, sphereIds);

      return subscriptions.map((s) => ({
        ...s,
        sphereName:
          nameMap.get(s.sphere_id) ||
          t('sphereChannels.subscribedSpheres.unknownSphere'),
      }));
    },
    [apiClients, t]
  );

  const fetchPage = useCallback(
    async (offset: number, append: boolean) => {
      if (!apiClients?.schemaClient) return;

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const response = await getSubscribedSpheres(
          apiClients.schemaClient,
          channelId,
          { limit: PAGE_LIMIT, offset }
        );

        const enriched = await enrichWithNames(response.data);
        setTotal(response.total);

        if (append) {
          setItems((prev) => [...prev, ...enriched]);
        } else {
          setItems(enriched);
        }
        offsetRef.current = offset + response.data.length;
      } catch {
        setError(t('sphereChannels.errors.loadFailed'));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [apiClients, channelId, orgId, enrichWithNames, t]
  );

  useEffect(() => {
    offsetRef.current = 0;
    fetchPage(0, false);
  }, [fetchPage, refreshTrigger]);

  const handleLoadMore = useCallback(() => {
    fetchPage(offsetRef.current, true);
  }, [fetchPage]);

  const handleRemove = useCallback(
    async (subscription: EnrichedSubscription) => {
      if (!apiClients?.schemaClient) return;

      const confirmed = globalThis.confirm(
        t('sphereChannels.subscribedSpheres.removeConfirm')
      );
      if (!confirmed) return;

      setRemovingId(subscription.id);

      const previousItems = [...items];
      const previousTotal = total;
      setItems((prev) => prev.filter((s) => s.id !== subscription.id));
      setTotal((prev) => prev - 1);

      try {
        await unsubscribeSphereFromChannel(
          apiClients.schemaClient,
          subscription.id,
          subscription.sphere_id
        );
        onUnsubscribed?.();
      } catch {
        setError(t('sphereChannels.subscribedSpheres.removeFailed'));
        setItems(previousItems);
        setTotal(previousTotal);
      } finally {
        setRemovingId(null);
      }
    },
    [apiClients, items, total, onUnsubscribed, t]
  );

  return (
    <div className="marketplace-subscribed-spheres-list">
      <h3 className="marketplace-subscribed-spheres-list__title">
        {t('sphereChannels.subscribedSpheres.title')}
      </h3>

      {error && (
        <div className="marketplace-subscribed-spheres-list__error">{error}</div>
      )}

      {loading && (
        <div className="marketplace-subscribed-spheres-list__loading">
          <i className="pi pi-spin pi-spinner" />
        </div>
      )}
      {!loading && items.length === 0 && (
        <div className="marketplace-subscribed-spheres-list__empty">
          {t('sphereChannels.subscribedSpheres.empty')}
        </div>
      )}
      {!loading && items.length > 0 && (
        <div className="marketplace-subscribed-spheres-list__items">
          {items.map((item) => (
            <div key={item.id} className="marketplace-subscribed-spheres-list__item">
              <i className="pi pi-globe marketplace-subscribed-spheres-list__item-icon" />
              <span className="marketplace-subscribed-spheres-list__item-name">
                {item.sphereName}
              </span>
              <span
                className={`marketplace-subscribed-spheres-list__scope-badge marketplace-subscribed-spheres-list__scope-badge--${item.scope_mode}`}
              >
                {item.scope_mode === 'recursive'
                  ? t('sphereChannels.subscribedSpheres.scopeRecursive')
                  : t('sphereChannels.subscribedSpheres.scopeDirect')}
              </span>
              {item.canManage && (
                <button
                  type="button"
                  className="marketplace-subscribed-spheres-list__remove-btn"
                  onClick={() => handleRemove(item)}
                  disabled={removingId === item.id}
                >
                  {removingId === item.id ? (
                    <i className="pi pi-spin pi-spinner" />
                  ) : (
                    t('sphereChannels.subscribedSpheres.remove')
                  )}
                </button>
              )}
            </div>
          ))}

          {hasNextPage && (
            <button
              type="button"
              className="marketplace-subscribed-spheres-list__load-more"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? (
                <i className="pi pi-spin pi-spinner" />
              ) : (
                t('sphereChannels.subscribedSpheres.loadMore')
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SubscribedSpheresListOrganism;
